import ExcelJS from 'exceljs';
import { downloadBlob } from '../utils/element';
import { Row } from '../utils';
import ApexCharts from 'apexcharts';

type Argument = {
  chartdata?: number[] | string[];
  chartRefs: { ref: string; sheetname: string }[];
  titles: string[];
  tablesheets?: Row[];
  tablesheetname: string;
};

export const useDownloadSheetChart = ({
  chartdata,
  chartRefs,
  titles,
  tablesheets,
  tablesheetname,
}: Argument): {
  downloadChart: () => Promise<void>;
  disabled: boolean;
} => {
  const fullBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin' },
    bottom: { style: 'thin' },
    left: { style: 'thin' },
    right: { style: 'thin' },
    diagonal: undefined,
  };

  const applyA4PageSetup = (worksheet: ExcelJS.Worksheet) => {
    worksheet.pageSetup = {
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      scale: undefined,
      horizontalCentered: true,
      verticalCentered: true,
      margins: {
        left: 0.25,
        right: 0.25,
        top: 0.5,
        bottom: 0.5,
        header: 0.3,
        footer: 0.3,
      },
    };

    worksheet.views = [
      {
        state: 'normal',
        zoomScale: 100,
      },
    ];
  };

  const getCellTextLength = (val: unknown): number => {
    const text = val?.toString() ?? '';

    if (!text) return 4;

    const isAllDigits = /^\d+$/.test(text);

    if (isAllDigits) {
      return text.length + 2;
    }

    const hasChinese = /[\u4E00-\u9FFF]/.test(text);

    if (hasChinese) {
      return 8;
    }

    return text.length + 2;
  };

  const exportToExcel = async () => {
    if (!chartdata?.length) {
      return;
    }

    const workbook = new ExcelJS.Workbook();

    // =========================
    // 統計表
    // =========================
    if (tablesheets && tablesheets.length > 0) {
      const worksheet = workbook.addWorksheet(tablesheetname);

      applyA4PageSetup(worksheet);

      tablesheets.forEach((row, rowIndex) => {
        const excelRowIndex = rowIndex + 2;
        const newRow = worksheet.getRow(excelRowIndex);

        let currentColIndex = 1;

        row.cells.forEach((cell) => {
          while (
            worksheet.getCell(
              excelRowIndex,
              currentColIndex
            ).isMerged
          ) {
            currentColIndex++;
          }

          const newCell =
            newRow.getCell(currentColIndex);

          newCell.value = cell.value ?? '';
          newCell.numFmt = '@';

          const styleObject: Partial<ExcelJS.Style> = {
            font: {
              ...(cell.style?.font || {}),
              size: 10,
            },
            alignment: {
              ...(cell.style?.alignment || {}),
              wrapText: true,
              vertical: 'middle',
              horizontal:
                cell.style?.alignment?.horizontal === 'left'
                  ? 'left'
                  : 'center',
            },
            border: cell.style?.border,
            numFmt: '@',
          };

          if (
            cell.style?.fill &&
            typeof cell.style.fill === 'object' &&
            'patternType' in cell.style.fill
          ) {
            styleObject.fill = {
              type: 'pattern',
              pattern:
                cell.style.fill.patternType || 'solid',
              fgColor: cell.style.fill.fgColor,
              bgColor: cell.style.fill.bgColor,
            };
          }

          newCell.style = styleObject;

          if (cell.merge) {
            const {
              row: mergeRows = 1,
              column: mergeCols = 1,
            } = cell.merge;

            worksheet.mergeCells(
              excelRowIndex,
              currentColIndex,
              excelRowIndex + mergeRows - 1,
              currentColIndex + mergeCols - 1
            );

            currentColIndex += mergeCols;
          } else {
            currentColIndex++;
          }
        });
      });

      const totalCols = worksheet.columnCount;

      // =========================
      // 標題
      // =========================
      if (totalCols > 0) {
        worksheet.mergeCells(
          1,
          1,
          1,
          totalCols
        );

        const headCell =
          worksheet.getCell('A1');

        headCell.value =
          titles[0] || '匯出結果';

        headCell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
        };

        headCell.font = {
          size: 14,
          bold: true,
        };
      }

      // =========================
      // Footer
      // =========================
      const lastDataRow =
        worksheet.lastRow?.number ?? 1;

      const footerRow =
        worksheet.getRow(lastDataRow + 1);

      if (totalCols > 0) {
        worksheet.mergeCells(
          footerRow.number,
          1,
          footerRow.number,
          totalCols
        );
      }

      footerRow.getCell(1).value =
        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　';

      footerRow.getCell(1).alignment = {
        horizontal: 'left',
        vertical: 'middle',
      };

      // =========================
      // 欄寬
      // =========================
      const minColWidth = 6;
      const maxColWidth = 12;

      const colWidths: number[] = [];

      worksheet.columns.forEach((col, i) => {
        let maxLength = minColWidth;

        col.eachCell?.(
          {
            includeEmpty: true,
          },
          (cell) => {
            const length =
              getCellTextLength(cell.value);

            if (length > maxLength) {
              maxLength = length;
            }
          }
        );

        colWidths[i] = Math.min(
          maxLength,
          maxColWidth
        );
      });

      worksheet.columns.forEach((col, i) => {
        col.width = colWidths[i] || 8;
      });

      // =========================
      // 列高 / 字體
      // =========================
      worksheet.properties.defaultRowHeight = 18;
      worksheet.pageSetup.printTitlesRow = '1:2';

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= 2) {
          return;
        }

        let maxLines = 1;

        row.eachCell((cell) => {
          cell.alignment = {
            ...cell.alignment,
            wrapText: true,
            vertical: 'middle',
          };

          const text = cell.value?.toString() ?? '';

          if (!text) return;

          const columnWidth =
            worksheet.getColumn(cell.col).width ?? 10;

          const estimatedLines = Math.max(
            1,
            Math.ceil(text.length / columnWidth)
          );

          maxLines = Math.max(
            maxLines,
            estimatedLines
          );
        });

        row.height = Math.min(
          Math.max(18, maxLines * 15),
          90
        );
      });

      // =========================
      // 全表框線
      // =========================
      const lastRow =
        worksheet.lastRow?.number ?? 0;

      const lastCol =
        worksheet.columnCount;

      for (let r = 1; r <= lastRow; r++) {
        const row = worksheet.getRow(r);

        for (let c = 1; c <= lastCol; c++) {
          row.getCell(c).border =
            fullBorder;
        }
      }
    }

    // =========================
    // 圖表
    // =========================
    for (
      let index = 0;
      index < chartRefs.length;
      index++
    ) {
      const chartId =
        chartRefs[index].ref;

      const chartInstance =
        ApexCharts.getChartByID(chartId);

      if (!chartInstance) {
        continue;
      }

      const dataURIResult =
        await chartInstance.dataURI();

      const imgURI =
        'imgURI' in dataURIResult
          ? dataURIResult.imgURI
          : '';

      if (!imgURI) {
        continue;
      }

      const worksheet =
        workbook.addWorksheet(
          chartRefs[index].sheetname
        );

      applyA4PageSetup(worksheet);

      worksheet.getCell('A1').value =
        titles[index] ?? titles[0];

      worksheet.getCell('A1').font = {
        bold: true,
        size: 14,
      };

      const base64 =
        imgURI.includes(',')
          ? imgURI.split(',')[1]
          : imgURI;

      const imageId =
        workbook.addImage({
          base64,
          extension: 'png',
        });

      worksheet.addImage(
        imageId,
        {
          tl: {
            col: 0,
            row: 1,
          },
          ext: {
            width: 794,
            height: 500,
          },
        }
      );
    }

    // =========================
    // 下載
    // =========================
    const buffer =
      await workbook.xlsx.writeBuffer();

    const blob =
      new Blob(
        [buffer],
        {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }
      );

    downloadBlob(
      blob,
      `${titles[0] || '匯出結果'}.xlsx`
    );
  };

  return {
    downloadChart: exportToExcel,
    disabled: !chartdata?.length,
  };
};

export default useDownloadSheetChart;